import React, { FC, ReactNode, useContext, useReducer, useState } from "react";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import ProjectService from "@app/projects/projectService";
import { toast } from "react-toastify";
import GapFillStatusEnum from "@app/projects/network-assessment/adequacy/gapFillStatusEnum";
import { Modal } from "@app/shared/Modals";
import { ResultRegion } from "@app/projects/results/common";
import SpecialtyGroupExceptionPanel, {
  getInitialFormState,
  mergeFormAndException,
} from "@app/projects/network-assessment/SpecialtyGroupExceptionPanel";
import { editExceptionFormReducer, normalizeSpaces } from "@app/exceptions/editExceptionReducer";
import { SpecialtyGroupException } from "@app/exceptions/interfaces";
import { SpecialtyGroupExceptionStatus } from "@app/exceptions/enums";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { UserContext } from "@app/user/UserContext";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextArea } from "@app/shared/qes-standards/forms/TextArea";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import GapFillStatusRecord from "@app/projects/network-assessment/gapFillStatusRecord";
import { GapFillStatusDescriptionMap } from "@app/projects/network-assessment/adequacy/gapFillStatusDescriptionMap";
import GapFillStatusHistoryTimeline from "@app/projects/network-assessment/GapFillStatusHistoryTimeline";

type GapFillStatusFlyoutProps = {
  clearCacheImpactAnalysisRunConfigurations: () => void;
  customProjectId: number;
  projectRunConfigurationId: number;
  record: GapFillStatusRecord;
  statusId: string;
  comment: string;
  gapFillDescriptionMap: GapFillStatusDescriptionMap;
  exception?: SpecialtyGroupException | null;
  exceptionIsEditable?: boolean;
  region?: ResultRegion;
  specialtyGroupName: string;
  specialtyGroupCode: string;
  onSubmitException?: (exception: SpecialtyGroupException) => void;
} & { children?: ReactNode };

export const GapFillStatusFlyout: FC<React.PropsWithChildren<GapFillStatusFlyoutProps>> = (props: GapFillStatusFlyoutProps) => {
  const flyoutContext = useContext(FlyoutContext);
  const userContext = useContext(UserContext);
  const projectService = new ProjectService();

  const originalStatus = props.record.gapFillStatus ?? GapFillStatusEnum.none;
  const [status, setStatus] = useState<GapFillStatusEnum>(originalStatus);

  const handleStatusChange = (status: GapFillStatusEnum) => {
    setStatus(status);
    if (userContext.roles.includes(Roles.EXCEPTION_MANAGER) && props.exceptionIsEditable && status === GapFillStatusEnum.exceptionRequired &&
      (!props.exception || props.exception?.specialtyGroupExceptionStatus === SpecialtyGroupExceptionStatus.Archived)) {
      setExceptionModalOpen(true);
    }
  }

  const [comment, setComment] = useState<string>((props.record.gapFillStatusComment || '').trim());
  const originalComment = props.record.gapFillStatusComment;

  const commentEnteredWithoutStatus = status === GapFillStatusEnum.none && !!comment && comment.length > 0;

  const [exceptionModalOpen, setExceptionModalOpen] = useState<boolean>(false);
  const [exceptionFormState, exceptionFormDispatch] = useReducer(editExceptionFormReducer, getInitialFormState(props.exception));
  const [exceptionToSave, setExceptionToSave] = useState<SpecialtyGroupException | null>(null);

  const handleSubmitException = () => {
    const result = mergeFormAndException(props.exception, exceptionFormState, props.region, props.specialtyGroupCode);
    setExceptionToSave(result);
    setExceptionModalOpen(false);
  }

  const [isSaving, setIsSaving] = useState<boolean>(false);

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      let commentToSave = comment;
      if (status === GapFillStatusEnum.none && comment.length > 0) {
        commentToSave = "";
      } else {
        commentToSave = normalizeSpaces(commentToSave);
      }
      await projectService.updateGapFillStatusWithRegionSetAsync(props.customProjectId, props.projectRunConfigurationId, props.record.regionSetId, props.record.specialtyGroupCode, status, commentToSave);

      if (!!exceptionToSave) {
        props.onSubmitException(exceptionToSave);
      }

      props.clearCacheImpactAnalysisRunConfigurations();
      toast(`Gap Resolution Status successfully updated${!!exceptionToSave ? " and Exception saved" : ""}!`,
        {
          containerId: "z-index-9999",
          type: "success"
        }
      );
    } catch (e) {
      toast(`Gap Resolution Status${!!exceptionToSave ? " and Exception" : ""} failed to update!`,
        {
          containerId: "z-index-9999",
          type: "warning"
        }
      );
    } finally {
      setIsSaving(false);
      flyoutContext.handleCloseFlyout();
    }
  }

  return (
    <>
      <Modal
        closeModal={() => {
          setExceptionModalOpen(false)
        }}
        content={{
          body: <SpecialtyGroupExceptionPanel customProjectId={props.customProjectId} exception={props.exception} formState={exceptionFormState}
            formDispatch={exceptionFormDispatch}
            specialtyGroupName={props.specialtyGroupName}
            region={props.region}
            specialtyGroupCode={props.specialtyGroupCode}
            expandedColumnSizes={true}
          />,
          cancelButtonText: "Cancel",
          confirmButtonAction: handleSubmitException,
          confirmButtonText: "Submit",
          confirmButtonType: "primary",
          title: `Specialty Exception Information`,
        }}
        isVisible={exceptionModalOpen}
        modalHeight={100}
      />
      <RoleRestricted allowedRoles={[Roles.EXCEPTION_MANAGER]}>
        {!!props.exception && props.exception.specialtyGroupExceptionStatus !== SpecialtyGroupExceptionStatus.Archived &&
          <div className="row">
            <div className="col-8 mb-3">
              <QESAlert type="warning" persist={true}>
                An exception currently exists on this specialty and county combination. If you would like to modify an
                existing exception, please edit within the specific specialty project or in Exceptions view.
              </QESAlert>
            </div>
          </div>
        }
      </RoleRestricted>
      <div className="row">
        <div className="col-8 mb-3">
          {commentEnteredWithoutStatus &&
            <QESAlert type="warning" persist={true}>
              Select a status to save.
            </QESAlert>
          }
          <QESSelect
            id={props.statusId}
            className="form-control"
            value={status}
            onChange={(e) => handleStatusChange(e.target.value as GapFillStatusEnum)}
            label="Status"
            testId="gap-fill-status-flyout__status-dropdown"
          >
            {Object.values(GapFillStatusEnum).map((value) => {
              return (
                <option key={value} value={value}>{props.gapFillDescriptionMap[value]}</option>
              );
            })}
          </QESSelect>
          <QESTextArea
            id={props.record.gapFillStatusComment}
            containerClassName="mt-3"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onBlur={() => setComment(comment => normalizeSpaces(comment))}
            maxLength={4000}
            label="Comments"
            testId="gap-fill-status-flyout__comment-input"
            disabled={status === GapFillStatusEnum.none}
          />
        </div>
      </div>

      {(!props.record.gapFillStatusUpdatedDate) ? (
        <div className="row">
          <div className="col-8 mb-3">
            No Gap Resolution Status currently exists. Select an option from the dropdown to change status.
          </div>
        </div>
      ) : (
        <AccordionContainer className="mt-3">
          <Accordion title="History Log" defaultOpen={!!props.record.gapFillStatusUpdatedDate}
            testId="gap-fill-status-flyout__history-log-accordion">
            {!!props.record.gapFillStatusUpdatedDate && (
              <GapFillStatusHistoryTimeline
                customProjectId={props.customProjectId}
                projectRunConfigurationId={props.projectRunConfigurationId}
                regionSetId={props.record.regionSetId}
                specialtyGroupCode={props.record.specialtyGroupCode}
                clearCacheImpactAnalysisRunConfigurations={() => props.clearCacheImpactAnalysisRunConfigurations()}
                gapFillStatusDescriptionMap={props.gapFillDescriptionMap}
              />
            )}
          </Accordion>
        </AccordionContainer>
      )}

      <FlyoutFooter
        submitButton={
          <QESButton
            qesType="primary"
            qesTestId="gap-fill-status-status-flyout__save"
            disabled={commentEnteredWithoutStatus || (originalStatus === status && normalizeSpaces(originalComment) === normalizeSpaces(comment)) || isSaving}
            onClick={handleSave}
          >
            Save
          </QESButton>}
      />
    </>
  );
}
